import React from 'react';
import {
  AddIcon,
  Button,
  LedgerHeader,
  WorkspacesIcon,
  ledgerContainerClass,
  type LedgerCollapse,
  type LedgerColumn,
} from '@billda/design-system';
import { LabourItemGroup } from '../../api/labourItemGroupsApi';
import { LabourGroupListItem } from '../molecules/LabourGroupListItem';
import { ListViewShell } from '../../../../shared/components/ui/ListViewShell';

interface LabourGroupsListViewProps {
  groups: LabourItemGroup[];
  loading: boolean;
  onItemClick: (id: string) => void;
  disabled?: boolean;
  onAddGroup?: () => void;
}

const GROUPS_COLLAPSE: LedgerCollapse = {
  container: 'labour-groups-collection',
  at: 620,
};

const GROUPS_COLUMNS: LedgerColumn[] = [
  { key: 'items', label: 'Labour items', flex: 0.8 },
  { key: 'includes', label: 'Includes', flex: 1.8, hideBelow: 760 },
];

export const LabourGroupsListView: React.FC<LabourGroupsListViewProps> = ({
  groups,
  loading,
  onItemClick,
  disabled,
  onAddGroup,
}) => {
  if (groups.length === 0 && !loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center p-8">
        <div className="flex max-w-[400px] flex-col items-center gap-4">
          <WorkspacesIcon
            aria-hidden="true"
            className="size-12 text-content-secondary"
          />
          <h6 className="type-h6 text-content-primary">No groups yet</h6>
          <p className="type-body-md text-center text-content-secondary">
            Labour groups organise items by trade (e.g. Brickwork, Joinery).
            Create groups to control which items workers see when booking.
          </p>
          {onAddGroup && (
            <Button
              variant="neutral"
              onClick={onAddGroup}
              data-testid="empty-state-add-group-button"
            >
              <AddIcon aria-hidden="true" className="size-4" />
              Add Group
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={ledgerContainerClass(GROUPS_COLLAPSE.container)}>
      <ListViewShell
        itemCount={groups.length}
        loading={loading}
        emptyMessage="No groups found"
      >
        <LedgerHeader
          columns={GROUPS_COLUMNS}
          collapse={GROUPS_COLLAPSE}
          titleLabel="Name"
          showValues={false}
          showActions
          hideBelow={GROUPS_COLLAPSE.at}
        />
        {groups.map(group => (
          <LabourGroupListItem
            key={group.id}
            group={group}
            columns={GROUPS_COLUMNS}
            collapse={GROUPS_COLLAPSE}
            onItemClick={onItemClick}
            disabled={disabled}
          />
        ))}
      </ListViewShell>
    </div>
  );
};
